import React, { useState, useEffect } from 'react';
import { Card, Button, Space, message, Alert, Row, Col, Input } from 'antd';
import { SaveOutlined, RobotOutlined } from '@ant-design/icons';
import apiClient from '../api';

const MasterBotConfigManager = ({ templates, loading, onUpdate }) => {
  const [config, setConfig] = useState(null);
  const [formData, setFormData] = useState({
    botNumber: '',
    defaultTemplateId: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await apiClient.get('/master-bot/config');
      if (response.data.success && response.data.config) {
        setConfig(response.data.config);
        setFormData({
          botNumber: response.data.config.botNumber || '',
          defaultTemplateId: response.data.config.defaultTemplateId || ''
        });
      }
    } catch (error) {
      console.error('Error loading master bot config:', error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.botNumber) {
      message.error('Bot number is required');
      return;
    }

    setSaving(true);
    try {
      const response = await apiClient.post('/master-bot/config', formData);
      if (response.data.success) {
        message.success('Master bot configuration saved successfully');
        loadConfig();
        if (onUpdate) onUpdate();
      } else {
        message.error(response.data.message || 'Failed to save configuration');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to save configuration');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const defaultAgent = templates.find(t => t.templateId === formData.defaultTemplateId);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <h2 style={{ margin: 0 }}>⚙️ Master Bot Configuration</h2>
            <p style={{ color: '#666', margin: '4px 0 0 0' }}>
              Configure the master bot's default agent and settings
            </p>
          </Col>
        </Row>
      </div>

      <Alert
        message="Master Bot System"
        description={
          <div>
            <p style={{ marginBottom: 8 }}>
              <strong>The master bot receives all messages and routes them intelligently:</strong>
            </p>
            <ol style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li>Check explicit user/group mappings first</li>
              <li>Use intelligent LLM routing if no mapping exists</li>
              <li>Fall back to default agent if routing fails</li>
            </ol>
            <p style={{ marginTop: 8, marginBottom: 0 }}>
              💡 The default agent is used when no other routing rule matches
            </p>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Card>
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            Master Bot Number <span style={{ color: 'red' }}>*</span>
          </label>
          <Input
            placeholder="e.g., 9999999999"
            value={formData.botNumber}
            onChange={(e) => setFormData({ ...formData, botNumber: e.target.value })}
            style={{ maxWidth: 400 }}
          />
          <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
            The WhatsApp number that receives all messages (master bot)
          </p>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            Default Agent Template
          </label>
          <select
            style={{ width: '100%', maxWidth: 400, padding: '8px', fontSize: 14, borderRadius: 4, border: '1px solid #d9d9d9' }}
            value={formData.defaultTemplateId}
            onChange={(e) => setFormData({ ...formData, defaultTemplateId: e.target.value })}
          >
            <option value="">No default (use fallback)</option>
            {templates.map(template => (
              <option key={template.templateId} value={template.templateId}>
                {template.templateId} - {template.agentName}
              </option>
            ))}
          </select>
          <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
            Agent to use when no explicit mapping or intelligent routing matches
          </p>
          {defaultAgent && (
            <div style={{ marginTop: 8, padding: 12, background: '#f0f2f5', borderRadius: 4 }}>
              <Space>
                <RobotOutlined style={{ color: '#52c41a' }} />
                <strong>Selected:</strong> {defaultAgent.agentName}
                <span style={{ color: '#666' }}>({defaultAgent.templateId})</span>
              </Space>
            </div>
          )}
        </div>

        <Button
          type="primary"
          icon={<SaveOutlined />}
          size="large"
          onClick={handleSubmit}
          loading={saving}
          disabled={!formData.botNumber || templates.length === 0}
        >
          Save Configuration
        </Button>
      </Card>

      {config && (
        <Card style={{ marginTop: 24 }}>
          <h3>Current Configuration</h3>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <strong>Bot Number:</strong> {config.botNumber}
            </div>
            <div>
              <strong>Default Agent:</strong> {config.defaultAgentName || config.defaultTemplateId || 'Not set'}
            </div>
            <div>
              <strong>Status:</strong> {config.isActive ? 'Active' : 'Inactive'}
            </div>
            {config.updatedAt && (
              <div>
                <strong>Last Updated:</strong> {new Date(config.updatedAt).toLocaleString()}
              </div>
            )}
          </Space>
        </Card>
      )}

      {templates.length === 0 && (
        <Alert
          message="No Templates Available"
          description="You need to create at least one agent template before configuring the master bot."
          type="warning"
          showIcon
          style={{ marginTop: 24 }}
        />
      )}
    </div>
  );
};

export default MasterBotConfigManager;

